import type { CodeMigration, MigrationAxis } from './types.js';
import { compareVersions, parseVersion } from '../lib/smartstack-version.js';
import { appTsxMigration_v2_0_0_preview_381 } from './app-tsx-migration.js';
import { appTsxRetroactiveMigration } from './app-tsx-retroactive-migration.js';
import { i18nNeutralizeParallelInitMigration } from './i18n-neutralize-parallel-init-migration.js';
import { indexCssTailwindV4Migration } from './index-css-tailwind-v4-migration.js';
import { programCsAzureMonitorMigration } from './program-cs-azure-monitor-migration.js';
import { programCsMigrationsBeforeInitMigration } from './program-cs-migrations-before-init-migration.js';
import { smartstackThemePostinstallMigration } from './smartstack-theme-postinstall-migration.js';

/**
 * Registry of all code migrations
 * Migrations are applied in order from oldest to newest
 *
 * NOT registered, on purpose (they contradict what `ss init` still generates and
 * what the registered migrations install — registering them would make two
 * migrations undo each other on every upgrade):
 * - i18n-remove-deps-migration       — init still ships i18next + the language detector;
 * - port-alignment-migration         — init still writes VITE_PORT=3000 (the nginx/docker contract);
 * - remove-theme-workaround-migration — removes the postinstall patcher that
 *   smartstack-theme-postinstall-migration installs and `ss upgrade` provisions.
 * Register one only together with the matching change of the init templates.
 * (directory-build-props-version-migration was REMOVED: it pinned a literal <Version>,
 *  which would freeze the product version — ss upgrade wires the root package.json read
 *  by <VersionPrefix> instead, lib/client-version.ts.)
 */
const migrations: CodeMigration[] = [
  appTsxRetroactiveMigration,
  appTsxMigration_v2_0_0_preview_381,
  i18nNeutralizeParallelInitMigration,
  indexCssTailwindV4Migration,
  programCsAzureMonitorMigration,
  programCsMigrationsBeforeInitMigration,
  smartstackThemePostinstallMigration,
];

/** A version window on one axis: the project is at `from`, the upgrade brings it to `to`. */
export interface VersionWindow {
  from: string | null | undefined;
  to: string | null | undefined;
}

export type MigrationWindows = Partial<Record<MigrationAxis, VersionWindow>>;

/**
 * The FROM used when a project's version on an axis is unknown (no
 * `generator.cliVersion` in an old project): the floor every registered
 * window starts at, so retroactive repairs still reach it.
 */
export const UNKNOWN_VERSION_FLOOR = '1.0.0';

const AXIS_ORDER: Record<MigrationAxis, number> = { socle: 0, cli: 1 };

function known(version: string | null | undefined, fallback: string): string {
  return version && parseVersion(version) ? version : fallback;
}

/**
 * The migrations to apply for the given windows, one per axis. A migration is
 * evaluated ONLY on its own axis (an axis without a window applies nothing).
 * It applies when EITHER:
 * 1. standard upgrade — current ≤ migration.from AND target ≥ migration.to;
 * 2. retroactive fix — migration.from ≤ current ≤ migration.to.
 * Every migration is idempotent (its `detect()` is false once applied), so a
 * window that matches again is harmless.
 *
 * Order: socle migrations first, then cli, each by ascending `toVersion`.
 */
export function findApplicableMigrations(windows: MigrationWindows): CodeMigration[] {
  return migrations.filter((migration) => {
    const window = windows[migration.axis];
    if (!window) return false;
    const current = known(window.from, UNKNOWN_VERSION_FLOOR);
    const target = known(window.to, current);
    const isStandardUpgrade = compareVersions(current, migration.fromVersion) <= 0
      && compareVersions(target, migration.toVersion) >= 0;
    const isRetroactiveFix = compareVersions(current, migration.fromVersion) >= 0
      && compareVersions(current, migration.toVersion) <= 0;
    return isStandardUpgrade || isRetroactiveFix;
  }).sort((a, b) => AXIS_ORDER[a.axis] - AXIS_ORDER[b.axis] || compareVersions(a.toVersion, b.toVersion));
}

/**
 * Gets all registered migrations
 */
export function getAllMigrations(): CodeMigration[] {
  return [...migrations];
}

/**
 * Registers a new migration (useful for testing or dynamic registration)
 */
export function registerMigration(migration: CodeMigration): void {
  migrations.push(migration);
}
